import { useMemo, useState } from "react";
import clsx from "clsx";
import { getUserLocalLogin, type CurrentUser } from "@/lib/auth";
import {
  deleteSOrdnerNote,
  postSOrdnerNote,
  type SOrdnerNote,
} from "@/lib/sordnerOverview";
import { NoteComposer } from "@/projectOverview/notes/NoteComposer";
import { NoteMessageBody } from "@/projectOverview/notes/NoteMessageBody";
import { useNoteReferenceAction } from "@/projectOverview/notes/useNoteReferenceAction";
import type { DraftNoteReference } from "@/projectOverview/notes/types";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import type { ProjectNote } from "@/lib/projectOverview";

function fmtNoteTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function SOrdnerNotesSection({
  sordnerId,
  notes,
  user,
  isBuero,
  onChanged,
}: {
  sordnerId: number;
  notes: SOrdnerNote[];
  user: CurrentUser | null;
  isBuero: boolean;
  onChanged: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { handleRefClick, fallbackModal } = useNoteReferenceAction(0);

  const login = getUserLocalLogin(user);

  const displayNotes = useMemo(() => [...notes], [notes]);

  function noteOwnedByUser(note: ProjectNote): boolean {
    const author = String(note.created_by_login || "").toLowerCase();
    if (!author || !login) return false;
    return author === login.toLowerCase() || author.startsWith(`${login.toLowerCase()}@`);
  }

  async function handleSubmit(payload: {
    note: string;
    visibility: "all" | "buero";
    references: DraftNoteReference[];
  }) {
    setSaving(true);
    setError(null);
    try {
      await postSOrdnerNote(sordnerId, {
        note: payload.note,
        visibility: payload.visibility,
        references: payload.references.map((r) => ({
          type: r.type,
          ref_id: r.ref_id,
          label: r.label,
          trigger: r.trigger,
          token_text: r.token_text,
          start: r.start,
          end: r.end,
        })),
      });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(noteId: number | string) {
    if (!window.confirm("Notiz wirklich löschen?")) return;
    try {
      await deleteSOrdnerNote(sordnerId, noteId);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    }
  }

  return (
    <OverviewCard title="Notizen" fill className="eh-po-chat-panel">
      <div className="eh-po-chat-thread eh-po-notes-list">
        {!displayNotes.length ? (
          <p className="eh-po-chat-empty">Noch keine Notizen zu diesem S-Ordner.</p>
        ) : (
          displayNotes.map((note) => {
            const own = noteOwnedByUser(note);
            return (
              <article
                key={String(note.id)}
                className={clsx(
                  "eh-po-chat-bubble",
                  own && "eh-po-chat-bubble--own",
                  note.visibility === "buero" && "eh-po-chat-bubble--buero",
                )}
              >
                <header className="eh-po-chat-bubble-meta">
                  <span className="eh-po-chat-author">
                    {note.created_by_display || note.created_by_login || "Unbekannt"}
                  </span>
                  {note.created_at ? (
                    <time className="eh-po-chat-time" dateTime={note.created_at}>
                      {fmtNoteTime(note.created_at)}
                    </time>
                  ) : null}
                  {note.visibility === "buero" ? (
                    <span className="eh-po-chat-badge">Nur Büro</span>
                  ) : null}
                  {own ? (
                    <button
                      type="button"
                      className="eh-po-chat-delete"
                      title="Eigene Notiz löschen"
                      aria-label="Eigene Notiz löschen"
                      onClick={() => void handleDelete(note.id)}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden>
                        <path
                          fill="currentColor"
                          d="M9 3h6l1 2h4v2H4V5h4l1-2zm-2 6h2v9H7V9zm4 0h2v9h-2V9zm4 0h2v9h-2V9zM6 7h12l-1 14H7L6 7z"
                        />
                      </svg>
                    </button>
                  ) : null}
                </header>
                <div className="eh-po-chat-bubble-body">
                  <NoteMessageBody
                    note={note}
                    onRefClick={(type, id, label) => void handleRefClick(type, id, label)}
                  />
                </div>
              </article>
            );
          })
        )}
      </div>

      <NoteComposer
        orderId={0}
        isBuero={isBuero}
        saving={saving}
        error={error}
        enableOrderReferences={false}
        onSubmit={(payload) => void handleSubmit(payload)}
      />

      {fallbackModal}
    </OverviewCard>
  );
}
